import {
  type AiFieldCellLoadingState,
  AiFieldThinkingPlaceholder,
  AiSummaryEmptyOutcomePlaceholder
} from '@databases/database-ai-fields';
import { DatabaseValueViewContainer } from '@databases/styles';
import type { DatabaseAiSummaryEmptyOutcome } from '@databases/types/database';
import React, { type FunctionComponent, useCallback, useRef } from 'react';

import DatabaseValueView, { type DatabaseValueViewProps } from '../DatabaseValueView.tsx';

import { useAiSummaryCellHeightObserver } from './ai-summary-cell-height-observer.tsx';

const AiSummaryFieldReadView: FunctionComponent<
  DatabaseValueViewProps & {
    emptyOutcome?: DatabaseAiSummaryEmptyOutcome['kind'];
    loadingState: AiFieldCellLoadingState;
  }
> = ({ contentId, emptyOutcome, fieldId, entryId, loadingState, ...props }) => {
  const measurementRef = useRef<{
    contentId: string;
    entryId: string;
    fieldId: string;
    height?: number;
  }>({ contentId, entryId, fieldId });
  const measurements = useAiSummaryCellHeightObserver();
  const unregisterRef = useRef<() => void>();
  const isLoading = loadingState !== null;

  const containerRef = useCallback(
    (container: HTMLDivElement | null) => {
      unregisterRef.current?.();
      unregisterRef.current = undefined;
      if (container === null) {
        return;
      }

      if (
        measurementRef.current.contentId !== contentId ||
        measurementRef.current.entryId !== entryId ||
        measurementRef.current.fieldId !== fieldId
      ) {
        measurementRef.current = { contentId, entryId, fieldId };
      }

      if (isLoading) {
        const { height } = measurementRef.current;
        container.style.minHeight = height === undefined ? '' : `${height}px`;
        return;
      }

      container.style.minHeight = '';
      measurementRef.current.height = container.getBoundingClientRect().height;
      unregisterRef.current = measurements?.register(container, (height) => {
        measurementRef.current.height = height;
      });
    },
    [contentId, entryId, fieldId, isLoading, measurements]
  );

  return (
    <div ref={containerRef} data-testid="ai-summary-read-view">
      {loadingState !== null ? (
        <DatabaseValueViewContainer data-testid="database-value-view-container">
          <AiFieldThinkingPlaceholder loadingState={loadingState} />
        </DatabaseValueViewContainer>
      ) : emptyOutcome !== undefined ? (
        <DatabaseValueViewContainer data-testid="database-value-view-container">
          <AiSummaryEmptyOutcomePlaceholder outcome={emptyOutcome} />
        </DatabaseValueViewContainer>
      ) : (
        <DatabaseValueView
          {...props}
          contentId={contentId}
          fieldId={fieldId}
          entryId={entryId}
        />
      )}
    </div>
  );
};

export default AiSummaryFieldReadView;
